import React from 'react';
import { useAuth } from '../hooks/useAuth';

const LayerInfoPanel = ({ layer, isOpen, onClose }) => {
  const { makeAuthenticatedRequest } = useAuth();
  
  if (!isOpen || !layer) return null;

  const handleSharePointLinkClick = async (e) => {
    e.preventDefault();
    try {
      const response = await makeAuthenticatedRequest(
        `/api/sharepoint/sites/${encodeURIComponent(layer.sharepoint_permissions.site_id)}/drives/${encodeURIComponent(layer.sharepoint_permissions.drive_id)}/files/${encodeURIComponent(layer.sharepoint_permissions.file_id)}/link`
      );
      // The backend should redirect, but if it returns a URL, we can redirect manually
      if (response.ok) {
        // If the backend returns JSON with a URL, redirect to it
        const result = await response.json();
        if (result.webUrl) {
          window.open(result.webUrl, '_blank');
        }
      } else {
        console.error('Failed to get SharePoint link:', response.status);
      }
    } catch (error) {
      console.error('Error opening SharePoint link:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Unknown';
    }
  };

  return (
    <div className="layer-style-overlay">
      <div className="layer-style-panel">
        <div className="style-panel-header">
          <h3>Layer Information: {layer?.name}</h3>
          <button className="btn-close-style" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="style-panel-content">
          <div className="layer-info-section">
            <h4>Basic Information</h4>
            <div className="info-grid">
              <div className="info-item">
                <label>Layer Name:</label>
                <span>{layer.name}</span>
              </div>
              <div className="info-item">
                <label>Geometry Type:</label>
                <span>{layer.geometry_type}</span>
              </div>
              <div className="info-item">
                <label>Feature Count:</label>
                <span>{layer.feature_count?.toLocaleString() || 'Unknown'}</span>
              </div>
              <div className="info-item">
                <label>File Type:</label>
                <span>{layer.file_type || 'Unknown'}</span>
              </div>
              {layer.file_size && (
                <div className="info-item">
                  <label>File Size:</label>
                  <span>{formatFileSize(layer.file_size)}</span>
                </div>
              )}
            </div>
          </div>

          {layer.bbox && (
            <div className="layer-info-section">
              <h4>Spatial Extent</h4>
              <div className="info-grid">
                <div className="info-item">
                  <label>Min X (West):</label>
                  <span>{layer.bbox[0]?.toFixed(6)}</span>
                </div>
                <div className="info-item">
                  <label>Min Y (South):</label>
                  <span>{layer.bbox[1]?.toFixed(6)}</span>
                </div>
                <div className="info-item">
                  <label>Max X (East):</label>
                  <span>{layer.bbox[2]?.toFixed(6)}</span>
                </div>
                <div className="info-item">
                  <label>Max Y (North):</label>
                  <span>{layer.bbox[3]?.toFixed(6)}</span>
                </div>
              </div>
            </div>
          )}

          {layer.crs && (
            <div className="layer-info-section">
              <h4>Coordinate Reference System</h4>
              <div className="info-grid">
                <div className="info-item">
                  <label>CRS:</label>
                  <span>{layer.crs}</span>
                </div>
              </div>
            </div>
          )}

          {layer.created_at && (
            <div className="layer-info-section">
              <h4>Metadata</h4>
              <div className="info-grid">
                <div className="info-item">
                  <label>Created:</label>
                  <span>{formatDate(layer.created_at)}</span>
                </div>
                {layer.modified_at && (
                  <div className="info-item">
                    <label>Modified:</label>
                    <span>{formatDate(layer.modified_at)}</span>
                  </div>
                )}
                {layer.source_file && (
                  <div className="info-item">
                    <label>Source File:</label>
                    <span>{layer.source_file}</span>
                  </div>
                )}
                {layer.sharepoint_permissions && (
                  <div className="info-item">
                    <label>SharePoint Folder:</label>
                    <button 
                      onClick={handleSharePointLinkClick}
                      className="sharepoint-link"
                      type="button"
                    >
                      View in SharePoint Folder
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {layer.csv_data && (
            <div className="layer-info-section">
              <h4>CSV Configuration</h4>
              <div className="info-grid">
                {layer.csv_data.configurationData?.columns && (
                  <>
                    <div className="info-item">
                      <label>X Column:</label>
                      <span>{layer.csv_data.configurationData.columns.x}</span>
                    </div>
                    <div className="info-item">
                      <label>Y Column:</label>
                      <span>{layer.csv_data.configurationData.columns.y}</span>
                    </div>
                    {layer.csv_data.configurationData.columns.label && (
                      <div className="info-item">
                        <label>Label Column:</label>
                        <span>{layer.csv_data.configurationData.columns.label}</span>
                      </div>
                    )}
                  </>
                )}
                {layer.csv_data.configurationData?.crs && (
                  <div className="info-item">
                    <label>Source CRS:</label>
                    <span>{layer.csv_data.configurationData.crs}</span>
                  </div>
                )}
                {layer.csv_data.configurationData?.hasHeaders !== undefined && (
                  <div className="info-item">
                    <label>Has Headers:</label>
                    <span>{layer.csv_data.configurationData.hasHeaders ? 'Yes' : 'No'}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="style-panel-actions">
          <button className="btn-close-style-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LayerInfoPanel;